import Layout from '../components/Layout';

const TOOLS = [
  {
    category: 'Onboarding & Strategy',
    items: [
      {
        name: 'Facts Agent',
        path: '/factsagent',
        description: 'A 6-step AI pipeline that extracts verified client facts from interview transcripts. Cleans, structures, and validates facility information including programs, staff, accreditations, amenities, insurance, and location data. Outputs a structured FactSheet used as the source of truth for all content generation.',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/file/d/13Z8_3JLES4RziICQNWZQGWEI6CpdyIZQ/view',
      },
      {
        name: 'ICP Tool',
        path: '/icp-agent',
        description: 'Generates a comprehensive Ideal Customer Profile from FactsAgent data. Analyzes facility facts to identify target demographics, pain points, decision drivers, and messaging strategies for treatment-center marketing.',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/file/d/1CTIj4DiLE36CnYQhncUlcrjh9H8w4GHm/view?usp=sharing',
      },
      {
        name: 'Website Audit',
        path: '/website-audit',
        description: 'Crawls a target website, scores SEO/UX/content gaps, surfaces missing pages from the topical map, and outputs a prioritized fix list with copy recommendations. Use it at kickoff to scope the engagement and at intervals to track health.',
        requiresProject: true,
      },
      {
        name: 'Topical Map',
        path: '/sop-topicalmap',
        description: 'AI-driven site architecture planner (Koray Semantic SEO 13-pass pipeline). Selects from a 937-topic master database, runs web research, deduplicates, and generates a 150-300 page site plan with titles, URLs, keywords, search intent, internal links, and publish phases. Click-to-sort and reorder columns; integrates with the Content Calendar.',
        requiresProject: true,
      },
      {
        name: 'Content Calendar',
        path: '/content-calendar',
        description: 'Schedule, track, and publish content across all sources (Topical Map, Website Audit, Content Writer, CW Non-BN). One unified queue with WP status, scheduled time, and live URL per entry. Bulk schedule + Slack notifications when posts go live.',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/file/d/1WEjp577XcDXXYTnn-8-nzkP4VdYP0-yE/view?usp=sharing',
      },
    ],
  },
  {
    category: 'Branding',
    items: [
      {
        name: 'Brand Kit',
        path: '/brand-kit',
        description: 'Per-project brand assets — logo, reference thumbnail, custom font, primary/secondary colors, and design templates. Style references are auto-extracted by Claude Vision and reused by every image-generation flow (CP Images, article thumbnails, infographics).',
        requiresProject: true,
      },
      {
        name: 'Image Generator',
        path: '/imagegenerator',
        description: 'AI image generator with two providers: Whisk Cookie (IMAGEN 3.5) and Nano Banana 2 (Imagen 4.0 via Google AI Studio). Text-to-image, reference image analysis with Gemini 2.5 Flash, gallery history, and EXIF metadata embedding.',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/file/d/1GnGaTAu1IfU6-qYs2-j75TvdlVprAJbm/view?usp=sharing',
      },
      {
        name: 'CP Images',
        path: '/cpimages',
        description: 'Automated branded marketing image generation. Two modes: Standard (parses uploaded content files for titles) and Custom (you type the titles directly or paste a CSV — useful for one-off image needs). Both modes pick a Program/Condition/SAP page type so the final export merges into the matching uploaded CSV with the right ACF column layout. Templates auto-detect their text area from the alpha channel; system + shared font fallback so titles render even with no project fonts uploaded. Generates WebP images with text overlays and Claude-generated alt text, downloads all as zip, exports CMS-ready CSVs.',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/file/d/1E3yug7t3cfIxujizttNX6aYVl-1WKNRL/view?usp=sharing',
      },
    ],
  },
  {
    category: 'Content Production',
    items: [
      {
        name: 'Content Writer',
        path: '/content-writer',
        description: 'Behavioral-health-tuned blog post pipeline. Single-call Holly Prompt or full 6-stage standard pipeline (research → outline → write → fact-check → verify → finalize). Generates thumbnail + infographics, edits in rich text, sends to Calendar, and publishes to WordPress with hierarchical CPT support.',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/file/d/1WEjp577XcDXXYTnn-8-nzkP4VdYP0-yE/view?usp=sharing',
      },
      {
        name: 'CW Non-BN',
        path: '/cw-non-bn',
        description: 'Niche-agnostic version of Content Writer. Same pipeline, send-to-calendar, image generation, and WordPress publishing — but works for any industry (fitness, SaaS, real estate, e-commerce, etc.) via a generic Holly prompt with optional Industry/Niche input. Data is stored separately from Content Writer.',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/file/d/1WEjp577XcDXXYTnn-8-nzkP4VdYP0-yE/view?usp=sharing',
      },
      {
        name: 'Master Framework',
        path: '/contentframeworkv2',
        description: '9-agent AI pipeline producing 16-section publication-ready treatment-center pages. Strategist creates a content brief, Semantic SEO builds a blueprint, Writer drafts, Self-Checker validates, Fact & Compliance and SEO Validator run in parallel, Conversion optimizes CRO, and Master Reviewer approves or sends back for revision. Supports program, condition, and SAP pages.',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/file/d/1eb6ZhnAg0tePiIlaqr1XkL33OgRas2iS/view',
      },
      {
        name: 'Custom Framework',
        path: '/contentframework-custom',
        description: 'Brief-driven content framework with flexible page structure. Define your own sections, page types, and agent pipelines for non-standard content needs. Use when Master Framework\'s 16-section template doesn\'t fit.',
        requiresProject: true,
      },
      {
        name: 'Bulk Brief to Content',
        path: '/briefcontent',
        description: 'Parallel content generation from writing briefs with 1-8 concurrent workers and AI QA scoring. Produces 50+ similar pages at scale with automatic anti-AI language filtering.',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/file/d/17_dn68m-O3zlCMxQ_6-qgNKqLPQS5olD/view?usp=sharing',
      },
    ],
  },
  {
    category: 'Publishing & Integration',
    items: [
      {
        name: 'WP Live Update',
        path: '/liveupdate',
        description: 'Direct ACF field editor for WordPress. Browse CPTs, edit fields inline, bulk update, and bulk find & replace with revert history. Auto-detects available CPTs and REST bases on connection. WordPress credentials configured here are reused by every publishing flow (Content Writer, CW Non-BN, Calendar, FAQ Injector).',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/file/d/15zHJJcGBsPw-HMMb5MuPxNHLEEBPtLia/view',
      },
      {
        name: 'FAQ Injector',
        path: '/faq-injector',
        description: 'Reads any WordPress post or CPT by URL, generates 5 question-first FAQs with Claude (40–60 words, 2–5 sentences, first sentence directly answers the question), appends them to the post body as H3 question + P answer, and writes FAQ JSON-LD schema. Detects Rank Math / Yoast and routes the schema through the right plugin path so it lands in <head> when possible — surfaces the exact method used (Rank Math head / Yoast block / inline body) in the result panel. Single-URL or bulk URL flows, full audit log with revert (restores the original post + clears the FAQ schema), and per-row + clear-all history delete buttons. Reuses the WP credentials saved under WP Live Update.',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/drive/folders/1QuBEqhDAovdSN3YYuuEo2Kf-Q_ckdQ6M',
      },
      {
        name: 'Live Payload Update',
        path: '/payloadagent',
        description: 'Pushes content to Payload CMS instances. 4-tab editor with Lexical JSON support, media upload, and only-modified-fields saving.',
        requiresProject: true,
        videoUrl: 'https://drive.google.com/file/d/1DgmisB1ociVA0Z1z9_8Lc6yOMPG2o0jm/view?usp=sharing',
      },
      {
        name: 'Google Drive Editor',
        path: '/gdriveeditor',
        description: 'Browse and edit Google Docs with 31+ AI rewrite agents for grammar correction, tone adjustment, SEO optimization, and content expansion. Save changes back to Drive or export as HTML.',
        requiresProject: false,
        videoUrl: 'https://drive.google.com/file/d/1YkU_qoT7Kcvwtq3yjk9eIeVB_j5OcEMo/view',
      },
      {
        name: 'Social Media',
        path: '/social-media',
        description: 'GHL-integrated social post generator for Facebook, LinkedIn, Instagram, and Twitter/X. Generate platform-optimized posts from ICP, facts, or blog URLs, then publish or schedule via GoHighLevel.',
        requiresProject: true,
      },
    ],
  },
  {
    category: 'AEO & Optimization',
    items: [
      {
        name: 'Article Update Angle',
        path: '/article-update',
        description: 'Analyzes existing content and recommends fresh angles, new sections, and updated information to improve Google rankings.',
        requiresProject: false,
        videoUrl: 'https://drive.google.com/file/d/1TmRqQSlfcZi0BuBCHTSsNfRbyOfXN7SA/view?usp=sharing',
      },
      {
        name: 'Brand Brain',
        path: '/brand-brain',
        description: '14-section brand intelligence report from automated web research. Covers market positioning, competitive landscape, sentiment, personas, and go-to-market strategy.',
        requiresProject: false,
      },
      {
        name: 'LLM Visibility',
        path: '/llm-visibility',
        description: 'Scores brand visibility across ChatGPT, Bing, Perplexity, and Google AI Overview with baseline tracking and competitor analysis.',
        requiresProject: false,
      },
      {
        name: 'LLM Block Test',
        path: '/llm-block-test',
        description: 'Pulls the active client list from Notion (with website + industry) and probes each site against the top 10 LLM crawlers (GPTBot, OAI-SearchBot, ChatGPT-User, ClaudeBot, Claude-User, PerplexityBot, Perplexity-User, Google-Extended, Applebot-Extended, Meta-ExternalAgent) at three layers: robots.txt, homepage <meta>/X-Robots-Tag, and an HTTP probe with each bot\'s real User-Agent. Distinguishes "site blocks bots" from "site blocks our prober\'s IP" via a baseline browser-UA probe, so Cloudflare/WAF-blocked sites surface as Inconclusive instead of false-positive Blocked. Saved results overwrite on re-check. Public share reports update live as sites are re-crawled — recipients see the latest verdict without you re-creating the share.',
        requiresProject: false,
      },
      {
        name: 'Intent & Volume',
        path: '/intent-volume',
        description: 'Upload keyword exports from GSC, Ahrefs, or Semrush to group keywords by search intent and funnel stage. Auto-detects the data source and produces intent cluster analysis.',
        requiresProject: false,
      },
    ],
  },
  {
    category: 'SEO Agents',
    items: [
      {
        name: 'Homepage SEO Checker',
        path: '/bahaagents/seo-checker',
        description: '102-point automated SEO audit covering entity signals, local SEO, trust factors, and schema markup.',
        requiresProject: false,
      },
      {
        name: 'Press Release Generator',
        path: '/bahaagents/press-release',
        description: 'AI-generated AP-style press releases for treatment-center link building.',
        requiresProject: false,
      },
      {
        name: 'GMB Post Generator',
        path: '/bahaagents/gmb-post',
        description: 'Auto-generates policy-compliant Google Business Profile posts with proper CTAs and local signals.',
        requiresProject: false,
      },
      {
        name: 'Schema Generator',
        path: '/bahaagents/schema-generator',
        description: 'Generates JSON-LD MedicalWebPage schema markup for all page types.',
        requiresProject: false,
      },
    ],
  },
  {
    category: 'SEO & Analytics',
    items: [
      {
        name: 'Clients Analytics (SEO Dashboard)',
        path: '/seo-dashboard',
        description: 'Unified SEO monitoring dashboard integrating Bing Webmaster, Google Search Console, Google Analytics, LocalViking GMB metrics, and automated Slack/Notion reporting. Requires API keys configured in server .env.',
        requiresProject: false,
      },
      {
        name: 'Holistic SEO',
        path: '/holistic-seo',
        description: 'Comprehensive SEO toolkit with SERP rank tracking, content brief generation, technical audit, keyword clustering, and competitor analysis powered by DataForSEO and Google APIs.',
        requiresProject: false,
      },
    ],
  },
  {
    category: 'Administration',
    items: [
      {
        name: 'Projects',
        path: '/',
        description: 'Central project management hub. Create and manage client projects, configure settings, and access all project-specific tools.',
        requiresProject: false,
      },
      {
        name: 'Settings',
        path: '/settings',
        description: 'Configure rewrite agents, manage default prompts, model selection, and adjust platform-wide settings.',
        requiresProject: false,
      },
      {
        name: 'User Management',
        path: '/users',
        description: 'Manage users and roles. Three roles: Super Admin (analytics + logs), Admin (full access + user management), Editor (content generation). Admin/Super Admin only.',
        requiresProject: false,
      },
      {
        name: 'Notion Workspace',
        path: '/notion-dashboard',
        description: 'Two-way sync between BrandNorth projects and a Notion workspace. Push project status, content pipelines, and reports into Notion for client visibility. Includes Onboarding Automation (provisions a per-client onboarding board from a template database — wires Blocking / Blocked-by relations from template Order numbers, picks per-team assignees, and copies arbitrarily-deep block trees via deferred PATCH) and People Workload (rolls up tasks per assignee with status, deadlines, and load).',
        requiresProject: false,
      },
      {
        name: 'Analytics Dashboard',
        path: '/analytics',
        description: 'Platform-wide usage and cost analytics — API call volume, token usage by tool/agent/model, cost breakdowns, and per-user activity. Super Admin only.',
        requiresProject: false,
      },
      {
        name: 'Agent Monitor',
        path: '/agent-monitor',
        description: 'Live view of running agent jobs across the platform — job status, queue depth, errors, and per-agent throughput. Super Admin only.',
        requiresProject: false,
      },
      {
        name: 'System Monitor',
        path: '/system-monitor',
        description: 'Server health metrics — CPU, memory, disk, active connections, and slow request log. Super Admin only.',
        requiresProject: false,
      },
      {
        name: 'User Activity Tracker',
        path: '/projects/{projectId}/activities',
        description: 'Per-project activity feed showing who generated, edited, published, or scheduled what and when. Useful for client reporting and audit trails.',
        requiresProject: true,
      },
    ],
  },
];

export default function KnowledgeBasePage() {
  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Knowledge Base</h1>
          <p className="text-gray-500 mt-1">
            All tools and their purpose in the BrandNorth Content Platform.
          </p>
        </div>

        {/* Tool Categories */}
        <div className="space-y-8">
          {TOOLS.map((group) => (
            <div key={group.category}>
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                {group.category}
                <span className="text-xs font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                  {group.items.length} tools
                </span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {group.items.map((tool) => (
                  <div
                    key={tool.name}
                    className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">{tool.name}</h3>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          tool.requiresProject
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-green-50 text-green-600'
                        }`}
                      >
                        {tool.requiresProject ? 'Project Required' : 'Standalone'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {tool.description}
                    </p>
                    {'videoUrl' in tool && tool.videoUrl && (
                      <a
                        href={tool.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                        Watch Video Tutorial
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
